/* eslint-disable react/no-array-index-key */
import { useMemo } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import Properties, { renderPageLink, renderSiteList } from '../../properties/Properties';
import usePanelDataService from '../../../services/PanelDataService';
import { stringProperty, linkProperty, listLinkProperty } from '../../properties/funcs';
import { getUpTimeDuration } from '../../../utilities/DateTimeUtilities';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';
import { accessPointFilterId, timeRangeFilterId, wirelessControllerFilterId } from '../../filters';

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        renderAsEntity: true,
        entityFields: [
            'name',
            'sysName',
            'ipAddress',
            'vendor',
            'deviceModel',
            'serialNumber',
            'location',
            'wirelessController',
            'wirelessControllerName',
            'macAddress',
            'swVersion',
            'hwVersion',
        ],
        metrics: ['uptime'],
    },
};

function usePropertiesColumns(data) {
    const wlcId = _.get(data, 'device.wirelessController');
    return useMemo(() => {
        const filters = [{ name: wirelessControllerFilterId, uuid: wlcId }];
        return [
            {
                properties: [
                    stringProperty(t`Name`, 'device.name'),
                    stringProperty(t`IP Address`, 'device.ipAddress'),
                    stringProperty(t`MAC Address`, 'device.macAddress'),
                    stringProperty(t`Location`, 'device.location'),
                    linkProperty(
                        t`Wireless Controller`,
                        'device.wirelessControllerName',
                        '/wireless-controller-detail',
                        filters,
                        [accessPointFilterId],
                        renderPageLink
                    ),
                    listLinkProperty(t`Sites`, 'device.sites', [], [accessPointFilterId], renderSiteList),
                ],
            },
            {
                properties: [
                    stringProperty(t`Vendor`, 'device.vendor'),
                    stringProperty(t`Model`, 'device.deviceModel'),
                    stringProperty(t`Up Time`, 'uptime', getUpTimeDuration),
                    stringProperty(t`Serial Number`, 'device.serialNumber'),
                    stringProperty(t`Software Version`, 'device.swVersion'),
                    stringProperty(t`Hardware Version`, 'device.hwVersion'),
                ],
            },
        ];
    }, [wlcId]);
}

export function AccessPointProperties({ data }) {
    const propertiesColumns = usePropertiesColumns(data);
    const classes = useStyles();
    return (
        <div className={classes.propertiesPanel}>
            {propertiesColumns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function AccessPointPropertiesPanel() {
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <AccessPointProperties data={chartData} />;
}

AccessPointPropertiesPanel.propTypes = panelPropTypes;

AccessPointPropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Access Point Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 150,
};

export default Panel({
    typeAlias: 'AccessPointAttributes',
    filters: {
        required: [timeRangeFilterId, accessPointFilterId],
        optional: [],
    },
})(AccessPointPropertiesPanel);
